import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { removeLoadingKey } from '../helpers/common'
import { toast } from 'react-toastify'

export const ProductCategoriesLoadingKeys = {
    getProductCategoriesOptions: "GET_PRODUCT_CATEGORIES",
    addProductCategoriesOptions: "ADD_PRODUCT_CATEGORIES",
    updateProductCategoriesByProduct: "UPDATE_PRODUCT_CATEGORIES",
    updateVendorByProduct: "UPDATE_VENDOR_BY_PRODUCT"
}

const initialState = {
    loading: false,
    loadingKeys: [],
    error: null,
    options: []
}

export const updateVendorByProductRequest = createAsyncThunk(
    'productCategories/updateVendorByProductRequest',
    async ({
      productId,
      newVendor,
      requestToUpdate
    }, { rejectWithValue, dispatch }) => {
      try {
        const { data } = await axios.post(`/api/v1/update-vendor-by-product`, { productId, newVendor });
        if (requestToUpdate) {
          await dispatch(requestToUpdate({ id: productId }))
        }
        return data;
      } catch (error) {
        if ('string' === typeof error.response.data) {
          return rejectWithValue({ error: error.response.data })
        } else {
          return rejectWithValue({ error: error.response.data.errors })
        }
      }
    }
);

export const updateProductCategoriesByProductRequest = createAsyncThunk(
    'productCategories/updateProductCategoriesByProductRequest',
    async ({
      productId,
      newCategory,
      requestToUpdate
    }, { rejectWithValue, dispatch }) => {
      try {
        const { data } = await axios.post(`/api/v1/update-category-by-product`, { productId, newCategory });
        if (requestToUpdate) {
          await dispatch(requestToUpdate({ id: productId }))
        }
        return data;
      } catch (error) {
        if ('string' === typeof error.response.data) {
          return rejectWithValue({ error: error.response.data })
        } else {
          return rejectWithValue({ error: error.response.data.errors })
        }
      }
    }
);

export const getProductCategoriesOptionsRequest = createAsyncThunk(
    'productCategories/getProductCategoriesOptionsRequest',
    async ({}, { rejectWithValue }) => {
      try {
        const { data } = await axios.get(`/api/v1/get-category-options`);
        return data;
      } catch (error) {
        if ('string' === typeof error.response.data) {
          return rejectWithValue({ error: error.response.data })
        } else {
          return rejectWithValue({ error: error.response.data.errors })
        }
      }
    }
)

export const addProductCategoriesOptionRequest = createAsyncThunk(
    'productCategories/addProductCategoriesOptionRequest',
    async ({
      value,
    }, { rejectWithValue, dispatch }) => {
      try {
        const { data } = await axios.post(`/api/v1/add-new-category`, { value });
        await dispatch(getProductCategoriesOptionsRequest({}));
        return data;
      } catch (error) {
        if ('string' === typeof error.response.data) {
          return rejectWithValue({ error: error.response.data })
        } else {
          return rejectWithValue({ error: error.response.data.errors })
        }
      }
    }
);

export const productCategoriesSlice = createSlice({
    name: 'productCategories',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      builder
        .addCase(getProductCategoriesOptionsRequest.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, ProductCategoriesLoadingKeys.getProductCategoriesOptions];
        })
        .addCase(getProductCategoriesOptionsRequest.fulfilled, (state, action) => {
            state.loading = false;
            state.options = action.payload?.options;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductCategoriesLoadingKeys.getProductCategoriesOptions, state.loadingKeys);
        })
        .addCase(getProductCategoriesOptionsRequest.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductCategoriesLoadingKeys.getProductCategoriesOptions, state.loadingKeys);
        })
        .addCase(addProductCategoriesOptionRequest.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, ProductCategoriesLoadingKeys.addProductCategoriesOptions];
        })
        .addCase(addProductCategoriesOptionRequest.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductCategoriesLoadingKeys.addProductCategoriesOptions, state.loadingKeys);
        })
        .addCase(addProductCategoriesOptionRequest.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductCategoriesLoadingKeys.addProductCategoriesOptions, state.loadingKeys);
        })
        .addCase(updateVendorByProductRequest.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, ProductCategoriesLoadingKeys.updateVendorByProduct];
        })
        .addCase(updateVendorByProductRequest.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductCategoriesLoadingKeys.updateVendorByProduct, state.loadingKeys);
            toast.success(
              "The vendor is updated.", 
              { position: "bottom-right" }
            );
        })
        .addCase(updateVendorByProductRequest.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductCategoriesLoadingKeys.updateVendorByProduct, state.loadingKeys);
            toast.error(
              `The vendor can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(updateProductCategoriesByProductRequest.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, ProductCategoriesLoadingKeys.updateProductCategoriesByProduct];
        })
        .addCase(updateProductCategoriesByProductRequest.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ProductCategoriesLoadingKeys.updateProductCategoriesByProduct, state.loadingKeys);
            toast.success(
              "The category is updated.", 
              { position: "bottom-right" }
            );
        })
        .addCase(updateProductCategoriesByProductRequest.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ProductCategoriesLoadingKeys.updateProductCategoriesByProduct, state.loadingKeys);
            toast.error(
              `The category can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
    }
})

export const {} = productCategoriesSlice.actions;

export default productCategoriesSlice.reducer
